/**
 * Photogrammetry OBJ exports store vertices relative to a local origin so the coordinates
 * stay small, and ship the origin in a sidecar file. The format is not standardised — the
 * reference dataset uses a Pix4D `*_offset.xyz` holding one line of three doubles, but
 * key=value and JSON variants show up too — so the parser accepts anything unambiguous
 * and reports what it understood.
 */
export interface MeshOffset {
  east: number;
  north: number;
  height: number;
  /** Where the value came from, shown in the UI so a wrong sidecar is easy to spot. */
  source: string;
}

const NUMBER = /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;

export function parseOffset(text: string, source = "offset file"): MeshOffset {
  const cleaned = stripComments(text);

  const fromJson = tryJson(cleaned, source);
  if (fromJson) return fromJson;

  const fromKeys = tryKeyValue(cleaned, source);
  if (fromKeys) return fromKeys;

  const numbers = cleaned.match(NUMBER)?.map(Number).filter(Number.isFinite) ?? [];
  if (numbers.length >= 3) {
    return { east: numbers[0], north: numbers[1], height: numbers[2], source };
  }
  if (numbers.length === 2) {
    // Some exporters omit the vertical shift entirely.
    return { east: numbers[0], north: numbers[1], height: 0, source: `${source} (no Z, assumed 0)` };
  }
  throw new Error(`Could not read an X/Y/Z offset from ${source}`);
}

function stripComments(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/(^|\s)(#|\/\/|;).*$/, ""))
    .join("\n");
}

function tryJson(text: string, source: string): MeshOffset | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const value: unknown = JSON.parse(trimmed);
    if (Array.isArray(value) && value.length >= 3 && value.every((v) => typeof v === "number")) {
      return { east: value[0], north: value[1], height: value[2], source };
    }
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      const east = pickNumber(record, ["x", "east", "easting", "e"]);
      const north = pickNumber(record, ["y", "north", "northing", "n"]);
      const height = pickNumber(record, ["z", "height", "elevation", "altitude", "h"]) ?? 0;
      if (east !== undefined && north !== undefined) return { east, north, height, source };
    }
  } catch {
    // Fall through to the looser parsers.
  }
  return null;
}

function tryKeyValue(text: string, source: string): MeshOffset | null {
  const map = new Map<string, number>();
  const pattern = /\b([a-zA-Z_]+)\s*[:=]\s*([-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)/g;
  for (const match of text.matchAll(pattern)) {
    map.set(match[1].toLowerCase(), Number(match[2]));
  }
  if (map.size === 0) return null;
  const record = Object.fromEntries(map) as Record<string, unknown>;
  const east = pickNumber(record, ["x", "east", "easting", "offsetx", "e"]);
  const north = pickNumber(record, ["y", "north", "northing", "offsety", "n"]);
  const height = pickNumber(record, ["z", "height", "elevation", "altitude", "offsetz", "h"]) ?? 0;
  if (east === undefined || north === undefined) return null;
  return { east, north, height, source };
}

function pickNumber(record: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  }
  return undefined;
}

/** True when a file name looks like an offset sidecar. */
export function looksLikeOffsetFile(name: string): boolean {
  return /(^|[_\-.])offset[^/\\]*\.(xyz|txt|json|prj|csv)$/i.test(name) || /\.offset$/i.test(name);
}
